import React, { useEffect, useMemo, useState } from 'react';
import { withStyles } from '@material-ui/styles';
import { CircularProgress } from '@material-ui/core';
import { isDefined, isUndefined } from '../../../utils/isDefined';
import { translate } from '../../../i18n/I18nProvider';
import DataRow from '../../Datarow/DataRow';
import styles from '../styles';
import flowRight from 'lodash.flowright';
import withSettings from '../../../utils/withSettings';
import CustomColorButton from '../../customButtons/CustomColorButton';
import mapFeatures, { DESCRIPTION_FIELD_OPTIONS } from '../actions/mapFeatures';
import withUserCheck from '../../../utils/withUserCheck';
import mapNumberToDisplay from '../../../utils/mapNumberToDisplay';
import TextFieldWithEditDialog from '../../textFieldWithEditDialog/TextFieldWithEditDialog';
import StatusCell from '../../statusCell/StatusCell';
import STATUS_BOX_OPTIONS from '../../../consts/statusBoxOptions';
import pickOptionsForStatusCell from '../../statusCell/actions/pickOptionsForStatusCell';
import ActionsButtons from './ActionsButtons';
import getExtraSimcardDetails from '../actions/getExtraSimcardDetails';
import { setStatusText } from './SimcardInfo';
import CustomAccordion from '../../customAccordion/CustomAccordion';

const ExtraSimcards = ({
  subscription,
  classes,
  settings,
  simcards,
  isAdminUser
}) => {
  const customClasses = useMemo(() => {
    return {
      dataRow: {
        root: classes.dataRow_root,
        value: classes.dataRow_value
      },
      descriptionField: {
        root: classes.descriptionField_root,
        text: classes.descriptionField_text
      }
    };
  }, [classes]);

  const extraSimcards = mapFeatures({ subscription, simcards });
  const entries = Object.entries(extraSimcards);

  const [selectedKey, setSelectedKey] = useState(
    entries.length > 0 ? entries[0][0] : null
  );
  const [detailsMap, setDetailsMap] = useState({});
  const [isFetching, setIsFetching] = useState(false);

  const activeKey = entries.find(([key]) => key === selectedKey)
    ? selectedKey
    : entries[0][0];

  const activeCard = { ...extraSimcards[activeKey], ...detailsMap[activeKey] };

  useEffect(() => {
    if (isUndefined(activeCard.icc)) return;
    (async () => {
      setIsFetching(true);
      const result = await getExtraSimcardDetails(
        activeCard.client_id,
        activeCard.icc
      );
      if (isDefined(result)) {
        setDetailsMap(obj => ({ ...obj, [activeKey]: result }));
      }
      setIsFetching(false);
    })();
    // eslint-disable-next-line
  }, [activeKey]);

  if (entries.length === 0) {
    return null;
  }

  return (
    <div>
      {entries.length > 1 && (
        <div className={classes.tabButtons}>
          {entries.map(([key], index) => {
            const isSelected = key === selectedKey;
            const isActive = extraSimcards[key].active;
            const opacity = isSelected || !isActive ? 1 : 0.45;
            return (
              <div key={key} style={{ opacity }}>
                <CustomColorButton
                  color={settings.colors.modal_header_text}
                  backgroundColor={
                    isSelected || isActive
                      ? settings.colors.modal_header
                      : 'lightgray'
                  }
                  onClick={() => setSelectedKey(key)}
                >
                  {translate('SIM_CARD.EXTRA')} {index + 1}
                </CustomColorButton>
              </div>
            );
          })}
        </div>
      )}
      {isDefined(activeCard?.msisdn) && (
        <DataRow
          label={translate('TABLE.NUMBER')}
          value={mapNumberToDisplay(activeCard.msisdn)}
          customClasses={customClasses.dataRow}
          dense
        />
      )}
      <TextFieldWithEditDialog
        {...DESCRIPTION_FIELD_OPTIONS}
        customClasses={customClasses.descriptionField}
        requestId={activeCard?.id}
        editEnabled={isAdminUser}
      />
      <DataRow
        label={translate('CARD_TYPE')}
        value={setStatusText({
          value: activeCard.data_card,
          additionalValue: activeCard.primary_card_id,
          field: 'data_card',
          isESimCard: activeCard?.isESimCard ?? false
        })}
        customClasses={customClasses.dataRow}
        dense
      />
      <DataRow
        label={translate('STATUS')}
        value={
          <StatusCell
            isTable={false}
            {...pickOptionsForStatusCell(
              STATUS_BOX_OPTIONS.SIMCARD_ACTIVE,
              activeCard.active
            )}
          />
        }
        customClasses={customClasses.dataRow}
        dense
      />
      <CustomAccordion labelId="MORE_INFO">
        <DataRow
          label={translate('SIMCARD_NUMBER')}
          value={activeCard?.icc}
          customClasses={customClasses.dataRow}
          dense
        />
        <DataRow
          label={'PIN 1'}
          value={activeCard?.pin1}
          customClasses={customClasses.dataRow}
          dense
        />
        <DataRow
          label={'PUK'}
          value={activeCard?.puk1}
          customClasses={customClasses.dataRow}
          dense
        />
      </CustomAccordion>
      {isFetching ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '8px 0'
          }}
        >
          <CircularProgress size={20} />
        </div>
      ) : (
        <ActionsButtons
          data={{
            simcard: activeCard,
            isESimCard: activeCard?.isESimCard ?? false,
            isIoTCard: activeCard?.isIoTCard ?? false,
            activationCode: activeCard?.activationCode
          }}
          subscription={subscription}
          fromExtraSimcards={true}
        />
      )}
    </div>
  );
};

export default flowRight(
  withUserCheck,
  withSettings,
  withStyles(styles)
)(ExtraSimcards);
